import React, { useState, useEffect } from 'react';
import {
    Box,
    Paper,
    Typography,
    Grid,
    Button,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Chip,
    TextField,
    CircularProgress,
    Alert,
    Stack,
} from '@mui/material';
import CompareArrowsIcon from '@mui/icons-material/CompareArrows';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ShieldIcon from '@mui/icons-material/Shield';
import { axiosInstance } from '../../../api';

const CARD_SX = {
    p: 2.5,
    borderRadius: '16px',
    bgcolor: '#fff',
    border: '1px solid #eef0f4',
    boxShadow: '0 4px 20px rgba(17,24,39,0.05)',
};

const BiometricReconciliationPreview = () => {
    const today = new Date().toISOString().split('T')[0];
    const [startDate, setStartDate] = useState(today);
    const [endDate, setEndDate] = useState(today);
    const [previewData, setPreviewData] = useState(null);
    const [loading, setLoading] = useState(false);

    const fetchPreview = async () => {
        setLoading(true);
        try {
            const res = await axiosInstance.get(`/api/attendance/biometric/reconciliation/?start_date=${startDate}&end_date=${endDate}`);
            setPreviewData(res.data);
        } catch (err) {
            console.error('Error fetching reconciliation preview:', err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchPreview();
    }, []);

    const getStatusChip = (st) => {
        if (st === 'MATCHED') {
            return <Chip icon={<CheckCircleOutlineIcon />} label="Matched" size="small" color="success" sx={{ fontWeight: 700 }} />;
        }
        if (st === 'BIOMETRIC_ONLY') {
            return <Chip label="Biometric Only" size="small" color="primary" sx={{ fontWeight: 700 }} />;
        }
        if (st === 'WEB_ONLY') {
            return <Chip label="Web Session Only" size="small" color="secondary" sx={{ fontWeight: 700 }} />;
        }
        return <Chip label={st} size="small" color="warning" sx={{ fontWeight: 700 }} />;
    };

    const summary = previewData?.summary || {};

    return (
        <Box sx={{ mt: 2 }}>
            {/* Absolute Protection Guarantee Alert */}
            <Alert
                icon={<ShieldIcon fontSize="inherit" />}
                severity="info"
                sx={{
                    mb: 3,
                    borderRadius: '12px',
                    bgcolor: '#eff6ff',
                    border: '1px solid #bfdbfe',
                    color: '#1e40af',
                    '& .MuiAlert-icon': { color: '#2563eb' },
                }}
            >
                <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
                    READ-ONLY RECONCILIATION PREVIEW (ZERO MUTATIONS)
                </Typography>
                <Typography variant="body2">
                    This preview compares staged biometric evidence against web attendance and assigned shifts.
                    Punches remain safely isolated in biometric staging. <strong>No changes are made to EmployeeAttendance, leave balances, or payroll ledgers.</strong>
                </Typography>
            </Alert>

            {/* Filter Bar */}
            <Paper elevation={0} sx={{ ...CARD_SX, mb: 3, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
                <Stack direction="row" spacing={2} alignItems="center">
                    <TextField
                        type="date"
                        size="small"
                        label="From Date"
                        value={startDate}
                        onChange={(e) => setStartDate(e.target.value)}
                        InputLabelProps={{ shrink: true }}
                    />
                    <TextField
                        type="date"
                        size="small"
                        label="To Date"
                        value={endDate}
                        onChange={(e) => setEndDate(e.target.value)}
                        InputLabelProps={{ shrink: true }}
                    />
                    <Button
                        variant="contained"
                        startIcon={<CompareArrowsIcon />}
                        onClick={fetchPreview}
                        disabled={loading}
                        sx={{
                            borderRadius: '10px',
                            textTransform: 'none',
                            fontWeight: 700,
                            background: 'linear-gradient(135deg,#3b82f6,#2f6bff)',
                        }}
                    >
                        {loading ? <CircularProgress size={20} color="inherit" /> : 'Generate Preview'}
                    </Button>
                </Stack>
            </Paper>

            {/* Metrics */}
            {previewData && (
                <Grid container spacing={2.5} sx={{ mb: 3 }}>
                    <Grid item xs={12} sm={6} md={3}>
                        <Paper elevation={0} sx={CARD_SX}>
                            <Typography variant="caption" sx={{ color: '#6b7280', fontWeight: 700, textTransform: 'uppercase' }}>
                                Total Evaluated Days
                            </Typography>
                            <Typography variant="h4" sx={{ fontWeight: 800, color: '#111827', mt: 0.5 }}>
                                {summary.total_evaluated_days || 0}
                            </Typography>
                        </Paper>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <Paper elevation={0} sx={CARD_SX}>
                            <Typography variant="caption" sx={{ color: '#6b7280', fontWeight: 700, textTransform: 'uppercase' }}>
                                Fully Matched (Bio + Web)
                            </Typography>
                            <Typography variant="h4" sx={{ fontWeight: 800, color: '#16a34a', mt: 0.5 }}>
                                {summary.matched_count || 0}
                            </Typography>
                        </Paper>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <Paper elevation={0} sx={CARD_SX}>
                            <Typography variant="caption" sx={{ color: '#6b7280', fontWeight: 700, textTransform: 'uppercase' }}>
                                Biometric Punches Only
                            </Typography>
                            <Typography variant="h4" sx={{ fontWeight: 800, color: '#2563eb', mt: 0.5 }}>
                                {summary.biometric_only_count || 0}
                            </Typography>
                        </Paper>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <Paper elevation={0} sx={CARD_SX}>
                            <Typography variant="caption" sx={{ color: '#6b7280', fontWeight: 700, textTransform: 'uppercase' }}>
                                Web Session Only
                            </Typography>
                            <Typography variant="h4" sx={{ fontWeight: 800, color: '#9333ea', mt: 0.5 }}>
                                {summary.web_only_count || 0}
                            </Typography>
                        </Paper>
                    </Grid>
                </Grid>
            )}

            {/* Reconciliation Comparison Table */}
            <Paper elevation={0} sx={{ ...CARD_SX, p: 0, overflow: 'hidden' }}>
                <TableContainer>
                    <Table>
                        <TableHead sx={{ bgcolor: '#f9fafb' }}>
                            <TableRow>
                                <TableCell sx={{ fontWeight: 700 }}>Employee</TableCell>
                                <TableCell sx={{ fontWeight: 700 }}>Attendance Date</TableCell>
                                <TableCell sx={{ fontWeight: 700 }}>Biometric (In / Out)</TableCell>
                                <TableCell sx={{ fontWeight: 700 }}>Web Session (In / Out)</TableCell>
                                <TableCell sx={{ fontWeight: 700 }}>Assigned Shift</TableCell>
                                <TableCell sx={{ fontWeight: 700 }}>Authoritative Attendance</TableCell>
                                <TableCell sx={{ fontWeight: 700 }}>Reconciliation Status</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {loading ? (
                                <TableRow>
                                    <TableCell colSpan={7} align="center" sx={{ py: 6 }}>
                                        <CircularProgress size={36} />
                                    </TableCell>
                                </TableRow>
                            ) : !previewData || previewData.rows?.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={7} align="center" sx={{ py: 6 }}>
                                        <Typography variant="body2" sx={{ color: '#6b7280' }}>
                                            No attendance or biometric activity found for the selected date range.
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            ) : (
                                previewData.rows.map((row, idx) => (
                                    <TableRow key={idx} hover>
                                        <TableCell>
                                            <Typography variant="body2" sx={{ fontWeight: 700, color: '#111827' }}>
                                                {row.employee_name}
                                            </Typography>
                                            <Typography variant="caption" sx={{ color: '#6b7280' }}>
                                                ID: {row.employee_no}
                                            </Typography>
                                        </TableCell>
                                        <TableCell sx={{ fontWeight: 600 }}>{row.date}</TableCell>
                                        <TableCell>
                                            {row.biometric_punches_count > 0 ? (
                                                <Box>
                                                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                                        {row.biometric_in || '--'} → {row.biometric_out || '--'}
                                                    </Typography>
                                                    <Typography variant="caption" sx={{ color: '#6b7280' }}>
                                                        {row.biometric_punches_count} punch(es)
                                                    </Typography>
                                                </Box>
                                            ) : (
                                                <Typography variant="caption" sx={{ color: '#9ca3af' }}>No punches</Typography>
                                            )}
                                        </TableCell>
                                        <TableCell>
                                            {row.web_in ? (
                                                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                                    {row.web_in} → {row.web_out || '(Open)'}
                                                </Typography>
                                            ) : (
                                                <Typography variant="caption" sx={{ color: '#9ca3af' }}>No web session</Typography>
                                            )}
                                        </TableCell>
                                        <TableCell>
                                            {row.assigned_shift ? (
                                                <Chip label={`${row.assigned_shift.code} (${row.assigned_shift.start_time}-${row.assigned_shift.end_time})`} size="small" variant="outlined" sx={{ fontWeight: 600, fontSize: '0.75rem' }} />
                                            ) : (
                                                <Typography variant="caption" sx={{ color: '#9ca3af' }}>Default General</Typography>
                                            )}
                                        </TableCell>
                                        <TableCell>
                                            <Chip label={row.existing_attendance_status} size="small" sx={{ fontWeight: 700, bgcolor: row.existing_attendance_status === 'PRESENT' ? '#dcfce7' : '#f3f4f6', color: row.existing_attendance_status === 'PRESENT' ? '#166534' : '#374151' }} />
                                        </TableCell>
                                        <TableCell>
                                            {getStatusChip(row.reconciliation_status)}
                                        </TableCell>
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Paper>
        </Box>
    );
};

export default BiometricReconciliationPreview;

